/**
 * FinancialAnalysisModal - AI-powered financial analysis of cashflow data
 * Saves analysis results and allows refreshing
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Info,
  DollarSign,
  CreditCard,
  Calendar,
  Loader2,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Clock,
  Save,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';
import { analyzeCashflow } from '../../services/geminiService';
import { cashflow } from '../../services/supabaseService';
import { format, formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

// Icon mapping
const ICONS = {
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'alert-triangle': AlertTriangle,
  'check-circle': CheckCircle,
  'info': Info,
  'dollar-sign': DollarSign,
  'credit-card': CreditCard,
  'calendar': Calendar,
};

// Health score colors
const HEALTH_COLORS = {
  excellent: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  good: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  fair: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  poor: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  critical: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
};

// Sentiment colors
const SENTIMENT_COLORS = {
  positive: 'text-emerald-400',
  neutral: 'text-gray-400',
  negative: 'text-red-400',
  warning: 'text-yellow-400',
};

// Alert colors
const ALERT_COLORS = {
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: 'text-blue-400' },
  warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: 'text-yellow-400' },
  danger: { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: 'text-red-400' },
};

// Priority colors
const PRIORITY_COLORS = {
  high: 'bg-red-500/20 text-red-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  low: 'bg-blue-500/20 text-blue-400',
};

// Simple markdown renderer for bold text
const renderMarkdown = (text) => {
  if (!text) return null;

  // Split by **text** pattern
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** and render as bold
      return <strong key={idx} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const FinancialAnalysisModal = ({ isOpen, onClose }) => {
  const {
    entries,
    summary,
    recurringParents,
    pastDebts,
    pastDebtsTotalTRY,
    currentMonth,
    exchangeRates,
    spendingAnalysis: creditCardAnalysis, // Credit card spending analysis
  } = useCashflowStore();

  const [analysis, setAnalysis] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSaved, setIsLoadingSaved] = useState(true);
  const [error, setError] = useState(null);

  const currentMonthStr = format(currentMonth, 'yyyy-MM');

  // Load saved analysis when modal opens
  useEffect(() => {
    if (isOpen) {
      loadSavedAnalysis();
    }
  }, [isOpen, currentMonthStr]);

  // Load saved analysis from database
  const loadSavedAnalysis = async () => {
    setIsLoadingSaved(true);
    setError(null);

    try {
      const saved = await cashflow.getAnalysis(currentMonthStr);

      if (saved) {
        // Convert from database format to display format
        setAnalysis({
          overallHealth: saved.overall_health,
          healthScore: saved.health_score,
          summary: saved.summary,
          sections: saved.sections,
          keyMetrics: saved.key_metrics,
          recommendations: saved.recommendations,
          alerts: saved.alerts,
        });
        setLastUpdated(new Date(saved.updated_at));
      } else {
        // No saved analysis, run new one
        setAnalysis(null);
        setLastUpdated(null);
      }
    } catch (err) {
      console.error('Error loading saved analysis:', err);
      // Don't set error, just proceed without saved data
    } finally {
      setIsLoadingSaved(false);
    }
  };

  // Run new analysis and save
  const runAnalysis = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get loan groups
      const loanGroups = await cashflow.getLoanGroups();

      // Prepare data for analysis
      const analysisData = {
        entries,
        summary,
        recurringParents,
        loanGroups,
        pastDebts: {
          totalTRY: pastDebtsTotalTRY,
          count: pastDebts?.length || 0,
        },
        currentMonth: currentMonthStr,
        exchangeRates,
        // Include credit card spending analysis if available
        creditCardAnalysis: creditCardAnalysis ? {
          healthScore: creditCardAnalysis.healthScore,
          overallAnalysis: creditCardAnalysis.overallAnalysis,
          spendingByCategory: creditCardAnalysis.spendingByCategory,
          subscriptions: creditCardAnalysis.subscriptions,
          topMerchants: creditCardAnalysis.topMerchants,
          monthlyBreakdown: creditCardAnalysis.monthlyBreakdown,
        } : null,
      };

      // Run AI analysis
      const result = await analyzeCashflow(analysisData);

      // Save to database
      await cashflow.saveAnalysis(currentMonthStr, result, {
        entriesCount: entries?.length || 0,
        summary: summary,
      });

      setAnalysis(result);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Analiz yapılırken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const healthColors = analysis ? HEALTH_COLORS[analysis.overallHealth] || HEALTH_COLORS.fair : HEALTH_COLORS.fair;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                AI Finansal Analiz
                <Sparkles className="w-4 h-4 text-purple-400" />
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{format(currentMonth, 'MMMM yyyy', { locale: tr })}</span>
                {lastUpdated && (
                  <>
                    <span className="text-gray-600">•</span>
                    <span className="flex items-center gap-1 text-xs">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(lastUpdated, { addSuffix: true, locale: tr })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runAnalysis}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-colors disabled:opacity-50"
              title="Yeni Analiz Yap"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="text-sm hidden sm:inline">Yenile</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoadingSaved ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-12 h-12 text-purple-400 animate-spin" />
              <p className="text-gray-400">Kaydedilmiş analiz yükleniyor...</p>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-12 h-12 text-purple-400 animate-spin" />
              <p className="text-gray-400">Finansal veriler analiz ediliyor...</p>
              <p className="text-xs text-gray-500">Bu işlem birkaç saniye sürebilir</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <AlertTriangle className="w-12 h-12 text-red-400" />
              <p className="text-red-400">{error}</p>
              <button
                onClick={runAnalysis}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Tekrar Dene
              </button>
            </div>
          ) : analysis ? (
            <div className="space-y-6">
              {/* Health Score Card */}
              <div className={`${healthColors.bg} ${healthColors.border} border rounded-xl p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Finansal Sağlık</p>
                    <p className={`text-3xl font-bold ${healthColors.text}`}>
                      {analysis.healthScore}/100
                    </p>
                    <p className={`text-sm ${healthColors.text} capitalize mt-1`}>
                      {analysis.overallHealth === 'excellent' && 'Mükemmel'}
                      {analysis.overallHealth === 'good' && 'İyi'}
                      {analysis.overallHealth === 'fair' && 'Orta'}
                      {analysis.overallHealth === 'poor' && 'Zayıf'}
                      {analysis.overallHealth === 'critical' && 'Kritik'}
                    </p>
                  </div>
                  <div className="w-24 h-24 relative">
                    <svg className="transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="8"
                        className="text-gray-700"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="8"
                        strokeDasharray={`${analysis.healthScore * 2.51} 251`}
                        className={healthColors.text}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-300 mt-4">{renderMarkdown(analysis.summary)}</p>
              </div>

              {/* Alerts */}
              {analysis.alerts && analysis.alerts.length > 0 && (
                <div className="space-y-2">
                  {analysis.alerts.map((alert, idx) => {
                    const colors = ALERT_COLORS[alert.type] || ALERT_COLORS.info;
                    return (
                      <div
                        key={idx}
                        className={`${colors.bg} ${colors.border} border rounded-lg p-3 flex items-start gap-3`}
                      >
                        <AlertTriangle className={`w-5 h-5 ${colors.icon} flex-shrink-0 mt-0.5`} />
                        <p className="text-gray-300 text-sm">{renderMarkdown(alert.message)}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Key Metrics */}
              {analysis.keyMetrics && analysis.keyMetrics.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {analysis.keyMetrics.map((metric, idx) => (
                    <div key={idx} className="bg-gray-800/50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">{metric.label}</p>
                      <p className={`text-lg font-semibold ${SENTIMENT_COLORS[metric.sentiment] || 'text-white'}`}>
                        {metric.value}
                      </p>
                      {metric.change && (
                        <p className={`text-xs ${metric.change.startsWith('+') ? 'text-emerald-400' : 'text-red-400'}`}>
                          {metric.change}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Analysis Sections */}
              {analysis.sections && analysis.sections.length > 0 && (
                <div className="space-y-4">
                  {analysis.sections.map((section, idx) => {
                    const IconComponent = ICONS[section.icon] || Info;
                    return (
                      <div key={idx} className="bg-gray-800/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <IconComponent className={`w-5 h-5 ${SENTIMENT_COLORS[section.sentiment] || 'text-gray-400'}`} />
                          <h3 className="font-medium text-white">{section.title}</h3>
                        </div>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{renderMarkdown(section.content)}</p>
                        {section.highlights && section.highlights.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {section.highlights.map((highlight, hIdx) => (
                              <span
                                key={hIdx}
                                className="text-xs bg-gray-700/50 text-gray-300 px-2 py-1 rounded"
                              >
                                {highlight}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recommendations */}
              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <div className="bg-gray-800/30 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    Öneriler
                  </h3>
                  <div className="space-y-3">
                    {analysis.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${PRIORITY_COLORS[rec.priority] || PRIORITY_COLORS.medium}`}>
                          {rec.priority === 'high' && 'Yüksek'}
                          {rec.priority === 'medium' && 'Orta'}
                          {rec.priority === 'low' && 'Düşük'}
                        </span>
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{rec.title}</p>
                          <p className="text-gray-400 text-xs mt-1">{renderMarkdown(rec.description)}</p>
                          {rec.impact && (
                            <p className="text-purple-400 text-xs mt-1 flex items-center gap-1">
                              <ChevronRight className="w-3 h-3" />
                              {rec.impact}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // No saved analysis - prompt to run one
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Brain className="w-16 h-16 text-gray-600" />
              <p className="text-gray-400 text-center">
                Bu ay için henüz analiz yapılmamış
              </p>
              <button
                onClick={runAnalysis}
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                Analiz Başlat
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default FinancialAnalysisModal;
